import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Formations from './pages/Formations';
import FormationDetail from './pages/FormationDetail';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import Devis from './pages/Devis';
import Formateurs from './pages/Formateurs';
import FormateurDetail from './pages/FormateurDetail';
import { OrganismesFormationList } from './pages/OrganismesFormation';
import OrganismeFormationDetail from './pages/OrganismesFormation';
import Stagiaires from './pages/Stagiaires';
import DevisForm from './pages/DevisForm';
import DevisDetail from './pages/DevisDetail';
import DevisClient from './pages/DevisClient';
import FicheInscriptionClient from './pages/FicheInscriptionClient';
import ConventionClient from './pages/ConventionClient';
import ContratFormateurPublic from './pages/ContratFormateurPublic';
import CommandeFormateurPublic from './pages/CommandeFormateurPublic';
import AmenagementEntreprise from './pages/AmenagementEntreprise';
import AmenagementAttestation from './pages/AmenagementAttestation';
import SignatureFormateurPublic from './pages/SignatureFormateurPublic';
import SignatureFicheProgresPublic from './pages/SignatureFicheProgresPublic';
import Parametres from './pages/Parametres';
import Prefacturation from './pages/Prefacturation';
import PrefactureDetail from './pages/PrefactureDetail';
import StagiaireDetail from './pages/StagiaireDetail';
import Calendrier from './pages/Calendrier';
import Utilisateurs from './pages/Utilisateurs';
import EmargementMobile from './pages/EmargementMobile';
import QuestionnairePublic from './pages/QuestionnairePublic';
import SatisfactionPublic from './pages/SatisfactionPublic';
import FormateurSession from './pages/FormateurSession';
import CRM from './pages/CRM';
import Reclamations from './pages/Reclamations';
import ReclamationDetail from './pages/ReclamationDetail';
import { OfflineBanner } from './components/OfflineBanner';
import { VisionneuseDocuments, BoutonInstallerApp } from './components/Shared';
import { SnapshotButton, SnapshotButtonMini } from './components/SnapshotButton';
import { SyncPanel } from './components/SyncPanel';
import { SnapshotProvider } from './context/SnapshotContext';
import './styles/App.css';

// ── Logo CEJ — icône mini (menu replié) ───────────────────────
function CejLogoMini() {
  return (
    <img src="/logo.png" alt="CEJ Concept"
      style={{ width: 46, height: 30, objectFit: 'contain', borderRadius: '6px' }} />
  );
}

// ── Logo CEJ — version complète (menu déroulé) ────────────────
function CejLogoFull() {
  return (
    <img src="/logo.png" alt="CEJ Concept"
      style={{ width: 168, height: 52, objectFit: 'contain', borderRadius: '8px' }} />
  );
}

// ── Icônes SVG de navigation ──────────────────────────────────
function NavIcon({ name }) {
  const p = {
    width: 20, height: 20, viewBox: '0 0 20 20',
    fill: 'none', stroke: 'currentColor',
    strokeWidth: 1.65, strokeLinecap: 'round', strokeLinejoin: 'round',
  };
  switch (name) {
    // Tableau de bord : 4 carrés
    case 'dashboard': return <svg {...p}>
      <rect x="2" y="2" width="7" height="7" rx="1.5"/>
      <rect x="11" y="2" width="7" height="7" rx="1.5"/>
      <rect x="2" y="11" width="7" height="7" rx="1.5"/>
      <rect x="11" y="11" width="7" height="7" rx="1.5"/>
    </svg>;

    // Sessions : calendrier avec lignes de liste
    case 'sessions': return <svg {...p}>
      <rect x="3" y="4" width="14" height="13" rx="2"/>
      <path d="M3 8h14M7 2v4M13 2v4"/>
      <path d="M6 12h3M6 15h5"/>
    </svg>;

    // Calendrier : calendrier avec points
    case 'calendrier': return <svg {...p}>
      <rect x="3" y="4" width="14" height="13" rx="2"/>
      <path d="M3 8h14M7 2v4M13 2v4"/>
      <circle cx="7" cy="12" r="1" fill="currentColor" stroke="none"/>
      <circle cx="10" cy="12" r="1" fill="currentColor" stroke="none"/>
      <circle cx="13" cy="12" r="1" fill="currentColor" stroke="none"/>
      <circle cx="7" cy="15.5" r="1" fill="currentColor" stroke="none"/>
      <circle cx="10" cy="15.5" r="1" fill="currentColor" stroke="none"/>
    </svg>;

    // Formations : chapeau de diplôme
    case 'formations': return <svg {...p}>
      <path d="M2 9l8-4.5 8 4.5-8 4.5z"/>
      <path d="M18 9v5"/>
      <path d="M5.5 11.5V15a8.5 2.8 0 0 0 9 0v-3.5"/>
    </svg>;

    // Clients : bâtiment
    case 'clients': return <svg {...p}>
      <path d="M3 18V7.5A2 2 0 0 1 5 5.5h10a2 2 0 0 1 2 2V18"/>
      <path d="M1 18h18"/>
      <rect x="7.5" y="13" width="5" height="5" rx="0.5"/>
      <path d="M7 9h2M11 9h2"/>
    </svg>;

    // Devis : document avec lignes + €
    case 'devis': return <svg {...p}>
      <rect x="4" y="2" width="12" height="16" rx="2"/>
      <path d="M7 6.5h6M7 9.5h6M7 12.5h3.5"/>
      <path d="M12.5 13v3M11 14.5h3"/>
    </svg>;

    // Formateurs : personne + coche
    case 'formateurs': return <svg {...p}>
      <circle cx="9" cy="7" r="4"/>
      <path d="M2 18c0-3.87 3.13-7 7-7"/>
      <path d="M13.5 14l1.5 1.5 3-3"/>
    </svg>;

    // Stagiaires : 2 personnes
    case 'stagiaires': return <svg {...p}>
      <circle cx="7.5" cy="7" r="3"/>
      <path d="M1 18c0-3.31 2.91-6 6.5-6"/>
      <circle cx="13.5" cy="7" r="3"/>
      <path d="M19 18c0-3.31-2.91-6-6.5-6"/>
      <path d="M10.5 13.5c1-.45 2-.6 3-.6"/>
    </svg>;

    // Préfacturation : document avec € et flèche export
    case 'prefacturation': return <svg {...p}>
      <rect x="4" y="2" width="12" height="16" rx="2"/>
      <path d="M7 6.5h6M7 9.5h6M7 12.5h3"/>
      <path d="M13 14v4M11 16h4"/>
    </svg>;

    // Paramètres : engrenage
    case 'parametres': return <svg {...p}>
      <circle cx="10" cy="10" r="3"/>
      <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.93 4.93l1.41 1.41M13.66 13.66l1.41 1.41M4.93 15.07l1.41-1.41M13.66 6.34l1.41-1.41"/>
    </svg>;

    // Utilisateurs : personne + clé
    case 'utilisateurs': return <svg {...p}>
      <circle cx="10" cy="7" r="4"/>
      <path d="M3 18c0-3.87 3.13-7 7-7s7 3.13 7 7"/>
    </svg>;

    // Déconnexion : porte de sortie
    case 'logout': return <svg {...p}>
      <path d="M14 8l4 4-4 4"/>
      <path d="M18 12H8"/>
      <path d="M10 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h5"/>
    </svg>;

    // CRM : entonnoir pipeline
    case 'crm': return <svg {...p}>
      <path d="M3 4h14l-5.5 6.5V15l-3-1.5V10.5z"/>
      <circle cx="15" cy="15" r="3"/>
      <path d="M15 12v1.5M13.7 16.3l1.1-1.1"/>
    </svg>;

    // Réclamations : bulle d'alerte
    case 'reclamations': return <svg {...p}>
      <rect x="2" y="3" width="16" height="12" rx="2"/>
      <path d="M6 19h8M10 15v4"/>
      <path d="M10 7v3M10 12v.5" strokeLinecap="round"/>
    </svg>;

    // Quitter : bouton power
    case 'power': return <svg {...p}>
      <path d="M10 2v8"/>
      <path d="M6.3 4.8a7 7 0 1 0 7.4 0"/>
    </svg>;

    // Organismes de Formation : bâtiment
    case 'of': return <svg {...p}>
      <rect x="2" y="9" width="16" height="10" rx="1"/>
      <path d="M5 9V6h10v3"/>
      <path d="M10 3v3"/>
      <path d="M7 13h2v6H7zM11 13h2v6h-2z"/>
    </svg>;

    default: return <svg {...p}><circle cx="10" cy="10" r="7"/></svg>;
  }
}

// ── Routes navigation ─────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/dashboard',  label: 'Tableau de bord', icon: 'dashboard',  roleMin: 'utilisateur', vue: 'dashboard'    },
  { path: '/calendrier', label: 'Planning',          icon: 'calendrier', roleMin: 'visiteur',    vue: 'calendrier'   },
  { path: '/sessions',   label: 'Sessions',         icon: 'sessions',   roleMin: 'visiteur',    vue: 'sessions'     },
  { path: '/devis',      label: 'Devis',            icon: 'devis',      roleMin: 'visiteur',    vue: 'devis'        },
  { path: '/clients',    label: 'Clients',          icon: 'clients',    roleMin: 'visiteur',    vue: 'clients'      },
  { path: '/stagiaires', label: 'Stagiaires',       icon: 'stagiaires', roleMin: 'visiteur',    vue: 'stagiaires'   },
  { path: '/formateurs',            label: 'Formateurs',               icon: 'formateurs', roleMin: 'visiteur', vue: 'formateurs' },
  { path: '/organismes-formation', label: 'Organismes de Formation', icon: 'of',         roleMin: 'visiteur', vue: 'formateurs' },
  { path: '/formations',     label: 'Formations',     icon: 'formations',     roleMin: 'visiteur',    vue: 'formations'   },
  { path: '/prefacturations', label: 'Préfacturation', icon: 'prefacturation', roleMin: 'visiteur',    vue: 'prefacturation' },
  { path: '/crm',            label: 'CRM',            icon: 'crm',            roleMin: 'visiteur',    vue: 'crm'           },
  { path: '/parametres',     label: 'Paramètres',     icon: 'parametres',     roleMin: 'visiteur',    vue: 'parametres'    },
];

function ProtectedRoute({ children, roleMin = 'visiteur', vue = null }) {
  const { user, loading, canAccess } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Seules les routes admin sont réellement restreintes — le RBAC gère le reste
  if (roleMin === 'admin' && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  if (vue && !canAccess(vue)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function FormateurLayout() {
  const { user, logout, formateurSessionId } = useAuth();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-main)', display: 'flex', flexDirection: 'column' }}>
      {/* Bandeau formateur */}
      <header style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', padding: '0.6rem 1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <svg viewBox="0 0 230 76" width="80" height="27" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="119" cy="46" rx="107" ry="32" fill="#828282"/>
            <ellipse cx="114" cy="38" rx="107" ry="32" fill="#F2A030"/>
            <text x="114" y="39" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="30" fill="#ffffff">CEJ Concept</text>
          </svg>
          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>|</span>
          <span style={{ color: '#E87722', fontWeight: 700, fontSize: '0.85rem' }}>🔑 Espace Formateur</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{user?.nom || 'Formateur'}</span>
          <button onClick={logout} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}>
            Déconnexion
          </button>
        </div>
      </header>
      <main style={{ flex: 1 }}>
        <OfflineBanner />
        <Routes>
          <Route path="/sessions/:id" element={<SessionDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={<Navigate to={`/sessions/${formateurSessionId}`} replace />} />
        </Routes>
      </main>
    </div>
  );
}

// Icône épingle SVG
function PinIcon({ pinned }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {pinned
        ? <><line x1="2" y1="2" x2="22" y2="22"/><path d="M10.58 10.58A7 7 0 0 0 12 21l1-5 5-1a7 7 0 0 0-7.42-5.42z"/><path d="M15 4l5 5-7.5 1.5-4-4z"/></>
        : <><path d="M12 21l1-5 5-1A7 7 0 0 0 5 8l-1 5z"/><path d="M9 3l5 5"/><line x1="12" y1="21" x2="12" y2="21"/></>
      }
    </svg>
  );
}

function AppLayout() {
  const { user, logout, hasRole, canAccess, loading, isFormateur } = useAuth();
  const [sidebarPinned, setSidebarPinned] = useState(
    () => localStorage.getItem('sidebar_pinned') === '1'
  );

  const togglePin = () => {
    setSidebarPinned(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_pinned', next ? '1' : '0');
      return next;
    });
  };

  if (loading) return null;
  if (!user) return <Login />;

  // Rediriger vers Paramètres si 2FA non configurée (et non exemptée par admin)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location2fa = useLocation();
  if (!isFormateur && !user.totp_enabled && !user.totp_bypass && location2fa.pathname !== '/parametres') {
    return <Navigate to="/parametres" replace state={{ setup2fa: true }} />;
  }

  // Layout dédié pour les formateurs (pas de sidebar, accès limité à leur session)
  if (isFormateur) return <FormateurLayout />;

  const visibleNav = NAV_ITEMS.filter(item => {
    if (item.roleMin === 'admin' && user.role !== 'admin') return false;
    if (item.vue && !canAccess(item.vue)) return false;
    return true;
  });
  // Dashboard si accessible, sinon première vue du menu
  const defaultPath = (user.role === 'admin' || canAccess('dashboard'))
    ? '/dashboard'
    : visibleNav.length > 0 ? visibleNav[0].path : '/sessions';

  const displayName = [user.prenom, user.nom].filter(Boolean).join(' ') || user.login || '';
  const initials = (
    (user.prenom?.[0] || '') + (user.nom?.[0] || '') || user.login?.[0] || '?'
  ).toUpperCase();

  async function handleQuit() {
    if (!window.confirm('Fermer l\'application et toutes les fenêtres ?')) return;
    try {
      const token = localStorage.getItem('gf_token');
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002/api'}/quit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* serveur déjà arrêté */ }
    window.close();
  }

  return (
    <SnapshotProvider>
    <div className={`app-layout${sidebarPinned ? ' app-layout--sidebar-pinned' : ''}`}>
      <aside className={`sidebar${sidebarPinned ? ' sidebar--pinned' : ''}`}>

        {/* En-tête logo */}
        <div className="sidebar-header">
          <button
            className={`pin-btn${sidebarPinned ? ' pin-btn--active' : ''}`}
            onClick={togglePin}
            title={sidebarPinned ? 'Déverrouiller le menu' : 'Verrouiller le menu ouvert'}
          >
            <PinIcon pinned={sidebarPinned} />
          </button>
          <div className="logo-mini"><CejLogoMini /></div>
          <div className="logo-full-area"><CejLogoFull /></div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {visibleNav.map(item => (
            <NavLink key={item.path} to={item.path}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              title={item.label}
              onClick={() => {
                const prefix = 'filter_' + item.path.replace('/', '') + '_';
                Object.keys(sessionStorage).filter(k => k.startsWith(prefix)).forEach(k => sessionStorage.removeItem(k));
              }}>
              <span className="nav-icon"><NavIcon name={item.icon} /></span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Pied de sidebar */}
        <div className="sidebar-footer">
          {/* Vue icônes (menu replié) */}
          <div className="footer-mini">
            <div className="user-avatar"
              title={`${displayName} · ${user.role}`}>
              {initials}
            </div>
            <SnapshotButtonMini />
            <button className="footer-icon-btn" onClick={logout} title="Déconnexion">
              <NavIcon name="logout" />
            </button>
            {hasRole('admin') && (
              <button className="footer-icon-btn footer-quit-btn" onClick={handleQuit} title="Quitter">
                <NavIcon name="power" />
              </button>
            )}
          </div>
          {/* Vue complète (menu déroulé au survol) */}
          <div className="footer-full">
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{displayName}</span>
              <span className={`sidebar-user-role role-${user.role}`}>{user.role}</span>
            </div>
            <SnapshotButton />
            <BoutonInstallerApp />
            <button className="btn-sidebar-action" onClick={logout}>🚪 Déconnexion</button>
            {hasRole('admin') && (
              <button className="btn-sidebar-action btn-sidebar-danger" onClick={handleQuit}>
                ⏻ Quitter
              </button>
            )}
            <span className="version-tag">v1.0 — CEJ CONCEPT</span>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <OfflineBanner />
        <Routes>
          <Route path="/" element={<Navigate to={defaultPath} replace />} />
          <Route path="/login" element={<Navigate to={defaultPath} replace />} />

          <Route path="/sessions"     element={<ProtectedRoute vue="sessions"><Sessions /></ProtectedRoute>} />
          <Route path="/sessions/:id" element={<ProtectedRoute vue="sessions"><SessionDetail /></ProtectedRoute>} />
          <Route path="/calendrier"   element={<ProtectedRoute vue="calendrier"><Calendrier /></ProtectedRoute>} />
          <Route path="/formations"   element={<ProtectedRoute vue="formations"><Formations /></ProtectedRoute>} />
          <Route path="/formations/:id" element={<ProtectedRoute vue="formations"><FormationDetail /></ProtectedRoute>} />

          <Route path="/dashboard"    element={<ProtectedRoute vue="dashboard"><Dashboard /></ProtectedRoute>} />
          <Route path="/clients"      element={<ProtectedRoute roleMin="visiteur" vue="clients"><Clients /></ProtectedRoute>} />
          <Route path="/clients/:id"  element={<ProtectedRoute roleMin="visiteur" vue="clients"><ClientDetail /></ProtectedRoute>} />
          <Route path="/devis"        element={<ProtectedRoute roleMin="visiteur" vue="devis"><Devis /></ProtectedRoute>} />
          <Route path="/devis/nouveau" element={<ProtectedRoute roleMin="utilisateur"><DevisForm /></ProtectedRoute>} />
          <Route path="/devis/:id"    element={<ProtectedRoute roleMin="visiteur" vue="devis"><DevisDetail /></ProtectedRoute>} />
          <Route path="/formateurs"   element={<ProtectedRoute roleMin="visiteur" vue="formateurs"><Formateurs /></ProtectedRoute>} />
          <Route path="/formateurs/:id" element={<ProtectedRoute roleMin="visiteur" vue="formateurs"><FormateurDetail /></ProtectedRoute>} />
          <Route path="/organismes-formation" element={<ProtectedRoute roleMin="visiteur" vue="formateurs"><OrganismesFormationList /></ProtectedRoute>} />
          <Route path="/organismes-formation/:id" element={<ProtectedRoute roleMin="visiteur" vue="formateurs"><OrganismeFormationDetail /></ProtectedRoute>} />
          <Route path="/stagiaires"   element={<ProtectedRoute roleMin="visiteur" vue="stagiaires"><Stagiaires /></ProtectedRoute>} />
          <Route path="/stagiaires/:id" element={<ProtectedRoute roleMin="visiteur" vue="stagiaires"><StagiaireDetail /></ProtectedRoute>} />

          <Route path="/prefacturations"     element={<ProtectedRoute vue="prefacturation"><Prefacturation /></ProtectedRoute>} />
          <Route path="/prefacturations/:id" element={<ProtectedRoute vue="prefacturation"><PrefactureDetail /></ProtectedRoute>} />

          <Route path="/crm"           element={<ProtectedRoute vue="crm"><CRM /></ProtectedRoute>} />

          <Route path="/reclamations"     element={<ProtectedRoute vue="reclamations"><Reclamations /></ProtectedRoute>} />
          <Route path="/reclamations/:id" element={<ProtectedRoute vue="reclamations"><ReclamationDetail /></ProtectedRoute>} />

          <Route path="/utilisateurs" element={<ProtectedRoute roleMin="admin"><Utilisateurs /></ProtectedRoute>} />
          <Route path="/parametres"   element={<ProtectedRoute><Parametres /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      </main>
    </div>

    {/* Panneau de synchronisation (overlay modal) */}
    <SyncPanel />
    </SnapshotProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginWrapper />} />
          <Route path="/devis-client/:token" element={<DevisClient />} />
          <Route path="/fiche-inscription/:token" element={<FicheInscriptionClient />} />
          <Route path="/fiche-inscription-directe/:token" element={<FicheInscriptionClient />} />
          <Route path="/convention-client/:token" element={<ConventionClient />} />
          <Route path="/contrat-formateur/:token" element={<ContratFormateurPublic />} />
          <Route path="/commande-formateur/:token" element={<CommandeFormateurPublic />} />
          <Route path="/amenagement-entreprise/:token" element={<AmenagementEntreprise />} />
          <Route path="/amenagement-attestation/:token" element={<AmenagementAttestation />} />
          <Route path="/signature-formateur/:token" element={<SignatureFormateurPublic />} />
          <Route path="/signature-fiche-progres/:token" element={<SignatureFicheProgresPublic />} />
          <Route path="/emargement/:token" element={<EmargementMobile />} />
          <Route path="/q/:token" element={<QuestionnairePublic />} />
          <Route path="/satisfaction/:token" element={<SatisfactionPublic />} />
          <Route path="/f/:token" element={<FormateurSession />} />
          <Route path="/*" element={<AppLayout />} />
        </Routes>
      </Router>
      <VisionneuseDocuments />
    </AuthProvider>
  );
}

function LoginWrapper() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}
